import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from './AppIcon';
import Select from './ui/Select';
import Button from './ui/Button';
import useAuth from 'hooks/useAuth';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch, user } = useAuth();
  const [isConnected, setIsConnected] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [selectedLocation, setSelectedLocation] = useState('');
  const [timePeriod, setTimePeriod] = useState('annual-action-plan');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [expandedRegions, setExpandedRegions] = useState({});
  const [expandedDistricts, setExpandedDistricts] = useState({});
  const mobileMenuRef = useRef(null);
  const locationDropdownRef = useRef(null);
  const profileMenuRef = useRef(null);
  const fullName = user?.user?.fullName || 'User';
  const initials = fullName.trim().charAt(0).toUpperCase();
  const roleSummary = useMemo(() => {
    const roleNames = (user?.user?.userRoles || [])
      .map((role) => String(role?.name || '').trim())
      .filter(Boolean);

    if (roleNames.length === 0) return 'No role assigned';
    if (roleNames.length === 1) return roleNames[0];
    return `${roleNames[0]} +${roleNames.length - 1}`;
  }, [user]);

  // Ghana administrative hierarchy data
  const ghanaHierarchy = {
    'AO Region': {
      'AO District 1': ['Community A1', 'Community A2', 'Community A3'],
      'AO District 2': ['Community B1', 'Community B2', 'Community B3']
    },
    'Ahafo Region': {
      'Tano North Municipal': ['Duayaw Nkwanta', 'Yamfo', 'Terchire'],
      'Tano South Municipal': ['Bechem', 'Derma', 'Techimantia']
    },
    'Ashanti Region': {
      'Adansi Asokwa': ['Asokwa Town', 'New Edubiase', 'Bodwesango'],
      'Adansi South': ['Aboabo', 'Aboabo New', 'Aboabo No 3', 'Abojemfo', 'Abotsewa', 'Achikrom', 'Adahumaso', 'Adamaso', 'Adansi Kenya', 'Adansi Praso']
    },
    'Asutifi North': {
      'Asutifi North Municipal': ['Kenyasi No 1', 'Kenyasi No 2', 'Acherensua'],
      'Asutifi South': ['Hwidiem', 'Ntotroso', 'Dadiesoaba']
    }
  };

  // Build hierarchical options for single dropdown
  const buildHierarchicalOptions = () => {
    const options = [];
    Object.keys(ghanaHierarchy)?.forEach(region => {
      // Add region
      options?.push({
        value: `region:${region}`,
        label: region,
        level: 'region',
        displayLabel: region
      });
      
      // Add districts under region
      Object.keys(ghanaHierarchy?.[region])?.forEach(district => {
        options?.push({
          value: `district:${region}:${district}`,
          label: district,
          level: 'district',
          displayLabel: `  ${district}`,
          parent: region
        });
        
        // Add communities under district
        ghanaHierarchy?.[region]?.[district]?.forEach(community => {
          options?.push({
            value: `community:${region}:${district}:${community}`,
            label: community,
            level: 'community',
            displayLabel: `    ${community}`,
            parent: district
          });
        });
      });
    });
    return options;
  };

  const hierarchicalOptions = buildHierarchicalOptions();

  const handleLocationChange = (value) => {
    setSelectedLocation(value);
  };

  const toggleRegion = (region) => {
    setExpandedRegions(prev => ({
      ...prev,
      [region]: !prev?.[region]
    }));
  };

  const toggleDistrict = (region, district) => {
    const key = `${region}:${district}`;
    setExpandedDistricts(prev => ({
      ...prev,
      [key]: !prev?.[key]
    }));
  };

  const getSelectedLocationDisplay = () => {
    if (!selectedLocation) return 'Select Location';
    const parts = selectedLocation?.split(':');
    if (parts?.[0] === 'region') return parts?.[1];
    if (parts?.[0] === 'district') return `${parts?.[1]} → ${parts?.[2]}`;
    if (parts?.[0] === 'community') return `${parts?.[1]} → ${parts?.[2]} → ${parts?.[3]}`;
    return 'Select Location';
  };

  const timePeriodOptions = [
    { value: 'annual-action-plan', label: 'Annual Action Plan Tracker' },
    { value: 'current-quarter', label: 'Current Quarter' },
    { value: 'last-quarter', label: 'Last Quarter' },
    { value: 'current-year', label: 'Current Year' },
    { value: 'last-year', label: 'Last Year' }
  ];

  const navigationItems = [
    // {
    //   label: 'District Data Plan',
    //   path: '/health-system-overview',
    //   icon: 'Activity',
    //   description: 'Strategic planning for data development and decision-making'
    // },
    {
      label: 'Program Performance',
      path: '/program-performance',
      icon: 'TrendingUp',
      description: 'Targeted health program analysis'
    },
    // {
    //   label: 'Facility Operations',
    //   path: '/facility-operations',
    //   icon: 'Building2',
    //   description: 'Operational efficiency and patient care quality'
    // },
    {
      label: 'Meetings',
      path: '/ghana/trackers',
      icon: 'Calendar',
      description: 'Tracker records from configured programs'
    },
    // {
    //   label: 'Tracker Registration',
    //   path: '/tracker-registration',
    //   icon: 'ClipboardList',
    //   description: 'Register health trackers for monitoring'
    // },
    // {
    //   label: 'Login',
    //   path: '/login',
    //   icon: 'LogIn',
    //   description: 'User authentication'
    // }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef?.current && !mobileMenuRef?.current?.contains(event?.target)) {
        setIsMobileMenuOpen(false);
      }
      if (locationDropdownRef?.current && !locationDropdownRef?.current?.contains(event?.target)) {
        setIsLocationDropdownOpen(false);
      }
      if (profileMenuRef?.current && !profileMenuRef?.current?.contains(event?.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen || isLocationDropdownOpen || isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen, isLocationDropdownOpen, isProfileMenuOpen]);

  const handleTabClick = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const formatLastUpdate = () => {
    const now = new Date();
    const diff = Math.floor((now - lastUpdate) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  const handleLogout = () => {
    setIsProfileMenuOpen(false);
    dispatch({ type: 'LOGOUT' });
    navigate('/login');
  };

  const handleSettingsNavigation = () => {
    setIsProfileMenuOpen(false);
    navigate('/ghana/settings');
  };

  return (
    <>
      <header className="header-container">
        <div className="header-content">
          <div className="header-logo">
            <div className="logo-icon">
              <Icon name="Activity" size={24} color="#FFFFFF" />
            </div>
            <span className="logo-text">DHIS2 Analytics</span>
          </div>

          <div className="global-context-selector hidden lg:flex">
            <div className="flex items-center gap-2">
              <Icon name="MapPin" size={16} className="text-muted-foreground" />
              <div className="relative" ref={locationDropdownRef}>
                <button
                  onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
                  className="flex items-center justify-between gap-2 px-3 py-2 bg-card border border-border rounded-md hover:bg-muted/50 transition-colors min-w-[300px] text-left"
                >
                  <span className="text-sm text-foreground truncate">{getSelectedLocationDisplay()}</span>
                  <Icon name="ChevronDown" size={16} className="text-muted-foreground flex-shrink-0" />
                </button>
                {isLocationDropdownOpen && (
                  <div className="absolute left-0 top-full mt-1 w-[400px] bg-card border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                    <div className="p-2">
                      {Object.keys(ghanaHierarchy)?.map((region) => (
                        <div key={region}>
                          {/* Region Level */}
                          <div className="flex items-center">
                            <button
                              onClick={(e) => {
                                e?.stopPropagation();
                                toggleRegion(region);
                              }}
                              className="p-2 hover:bg-muted/30 rounded transition-colors"
                            >
                              <Icon 
                                name={expandedRegions?.[region] ? "Minus" : "Plus"} 
                                size={14} 
                                className="text-muted-foreground" 
                              />
                            </button>
                            <button
                              onClick={() => {
                                handleLocationChange(`region:${region}`);
                                setIsLocationDropdownOpen(false);
                              }}
                              className={`flex-1 text-left px-3 py-2 rounded hover:bg-muted/50 transition-colors font-semibold ${
                                selectedLocation === `region:${region}` ? 'bg-primary/10 text-primary' : 'text-foreground'
                              }`}
                            >
                              {region}
                            </button>
                          </div>

                          {/* District Level */}
                          {expandedRegions?.[region] && Object.keys(ghanaHierarchy?.[region])?.map((district) => (
                            <div key={district}>
                              <div className="flex items-center" style={{ paddingLeft: '20px' }}>
                                <button
                                  onClick={(e) => {
                                    e?.stopPropagation();
                                    toggleDistrict(region, district);
                                  }}
                                  className="p-2 hover:bg-muted/30 rounded transition-colors"
                                >
                                  <Icon 
                                    name={expandedDistricts?.[`${region}:${district}`] ? "Minus" : "Plus"} 
                                    size={14} 
                                    className="text-muted-foreground" 
                                  />
                                </button>
                                <button
                                  onClick={() => {
                                    handleLocationChange(`district:${region}:${district}`);
                                    setIsLocationDropdownOpen(false);
                                  }}
                                  className={`flex-1 text-left px-3 py-2 rounded hover:bg-muted/50 transition-colors text-sm ${
                                    selectedLocation === `district:${region}:${district}` ? 'bg-primary/10 text-primary' : 'text-foreground'
                                  }`}
                                >
                                  <Icon name="ChevronRight" size={14} className="inline-block mr-1 text-muted-foreground" />
                                  {district}
                                </button>
                              </div>

                              {/* Community Level */}
                              {expandedDistricts?.[`${region}:${district}`] && ghanaHierarchy?.[region]?.[district]?.map((community) => (
                                <button
                                  key={community}
                                  onClick={() => {
                                    handleLocationChange(`community:${region}:${district}:${community}`);
                                    setIsLocationDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 rounded hover:bg-muted/50 transition-colors text-sm text-muted-foreground ${
                                    selectedLocation === `community:${region}:${district}:${community}` ? 'bg-primary/10 text-primary' : ''
                                  }`}
                                  style={{ paddingLeft: '64px' }}
                                >
                                  <Icon name="ChevronRight" size={14} className="inline-block mr-1 text-muted-foreground" />
                                  {community}
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <Select
              options={timePeriodOptions}
              value={timePeriod}
              onChange={setTimePeriod}
              placeholder="Select tracker"
              className="w-56"
            />
          </div>

          <div className="status-indicator-container">
            <div className="status-indicator">
              <span className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></span>
              <span className="status-text">{formatLastUpdate()}</span>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              iconName="Bell"
              iconSize={20}
              className="hidden lg:flex"
            />

            <div className="relative hidden lg:block" ref={profileMenuRef}>
              <button
                type="button"
                onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                className="inline-flex min-w-0 items-center gap-3 rounded-xl border border-border bg-background px-3 py-2 text-left transition-colors hover:bg-muted"
                title="Open user menu"
                aria-haspopup="menu"
                aria-expanded={isProfileMenuOpen}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white shrink-0">
                  {initials}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground">{fullName}</span>
                  <span className="block truncate text-xs text-muted-foreground">{roleSummary}</span>
                </span>
                <Icon
                  name={isProfileMenuOpen ? 'ChevronUp' : 'ChevronDown'}
                  size={16}
                  className="text-muted-foreground shrink-0"
                />
              </button>

              <div className={`dropdown-menu ${isProfileMenuOpen ? 'open' : ''}`}>
                <div className="px-4 py-3 border-b border-border">
                  <p className="text-sm font-semibold text-foreground truncate">{fullName}</p>
                  <p className="text-xs text-muted-foreground truncate">{roleSummary}</p>
                </div>

                <button type="button" onClick={handleSettingsNavigation} className="dropdown-item w-full text-left flex items-center gap-2">
                  <Icon name="Settings" size={16} />
                  User Settings
                </button>

                <button type="button" onClick={handleLogout} className="dropdown-item w-full text-left flex items-center gap-2 text-destructive">
                  <Icon name="LogOut" size={16} />
                  Logout
                </button>
              </div>
            </div>
          </div>

          <button
            className="mobile-menu-button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </header>
      <nav className="tab-navigation hidden lg:block">
        <div className="tab-navigation-content">
          <div className="tab-list">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleTabClick(item?.path)}
                className={`tab-item ${location?.pathname === item?.path ? 'active' : ''}`}
                title={item?.description}
              >
                <Icon name={item?.icon} size={18} className="inline-block mr-2" />
                {item?.label}
              </button>
            ))}
          </div>
        </div>
      </nav>
      <div
        ref={mobileMenuRef}
        className={`mobile-dropdown ${isMobileMenuOpen ? 'open' : ''}`}
      >
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex items-center gap-2">
            <Icon name="MapPin" size={16} className="text-muted-foreground" />
            <div className="relative flex-1">
              <button
                onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
                className="flex items-center justify-between gap-2 px-3 py-2 bg-card border border-border rounded-md hover:bg-muted/50 transition-colors w-full text-left"
              >
                <span className="text-sm text-foreground truncate">{getSelectedLocationDisplay()}</span>
                <Icon name="ChevronDown" size={16} className="text-muted-foreground flex-shrink-0" />
              </button>
              {isLocationDropdownOpen && (
                <div className="absolute left-0 top-full mt-1 w-full bg-card border border-border rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                  <div className="p-2">
                    {Object.keys(ghanaHierarchy)?.map((region) => (
                      <div key={region}>
                        {/* Region Level */}
                        <div className="flex items-center">
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              toggleRegion(region);
                            }}
                            className="p-2 hover:bg-muted/30 rounded transition-colors"
                          >
                            <Icon 
                              name={expandedRegions?.[region] ? "Minus" : "Plus"} 
                              size={14} 
                              className="text-muted-foreground" 
                            />
                          </button>
                          <button
                            onClick={() => {
                              handleLocationChange(`region:${region}`);
                              setIsLocationDropdownOpen(false);
                            }}
                            className={`flex-1 text-left px-3 py-2 rounded hover:bg-muted/50 transition-colors font-semibold ${
                              selectedLocation === `region:${region}` ? 'bg-primary/10 text-primary' : 'text-foreground'
                            }`}
                          >
                            {region}
                          </button>
                        </div>

                        {/* District Level */}
                        {expandedRegions?.[region] && Object.keys(ghanaHierarchy?.[region])?.map((district) => (
                          <div key={district}>
                            <div className="flex items-center" style={{ paddingLeft: '16px' }}>
                              <button
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  toggleDistrict(region, district);
                                }}
                                className="p-2 hover:bg-muted/30 rounded transition-colors"
                              >
                                <Icon 
                                  name={expandedDistricts?.[`${region}:${district}`] ? "Minus" : "Plus"} 
                                  size={14} 
                                  className="text-muted-foreground" 
                                />
                              </button>
                              <button
                                onClick={() => {
                                  handleLocationChange(`district:${region}:${district}`);
                                  setIsLocationDropdownOpen(false);
                                }}
                                className={`flex-1 text-left px-3 py-2 rounded hover:bg-muted/50 transition-colors text-sm ${
                                  selectedLocation === `district:${region}:${district}` ? 'bg-primary/10 text-primary' : 'text-foreground'
                                }`}
                              >
                                <Icon name="ChevronRight" size={14} className="inline-block mr-1 text-muted-foreground" />
                                {district}
                              </button>
                            </div>

                            {/* Community Level */}
                            {expandedDistricts?.[`${region}:${district}`] && ghanaHierarchy?.[region]?.[district]?.map((community) => (
                              <button
                                key={community}
                                onClick={() => {
                                  handleLocationChange(`community:${region}:${district}:${community}`);
                                  setIsLocationDropdownOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2 rounded hover:bg-muted/50 transition-colors text-sm text-muted-foreground ${
                                  selectedLocation === `community:${region}:${district}:${community}` ? 'bg-primary/10 text-primary' : ''
                                }`}
                                style={{ paddingLeft: '56px' }}
                              >
                                <Icon name="ChevronRight" size={14} className="inline-block mr-1 text-muted-foreground" />
                                {community}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <Select
            options={timePeriodOptions}
            value={timePeriod}
            onChange={setTimePeriod}
            placeholder="Select tracker"
          />
        </div>
        {navigationItems?.map((item) => (
          <div
            key={item?.path}
            onClick={() => handleTabClick(item?.path)}
            className={`mobile-dropdown-item ${location?.pathname === item?.path ? 'active' : ''}`}
          >
            <Icon name={item?.icon} size={18} className="inline-block mr-2" />
            {item?.label}
          </div>
        ))}
        <div className="border-t border-border p-4 space-y-2">
          <div className="px-1 pb-2">
            <p className="text-sm font-medium text-foreground">{fullName}</p>
            <p className="text-xs text-muted-foreground">{roleSummary}</p>
          </div>
          <button
            type="button"
            onClick={handleSettingsNavigation}
            className="mobile-dropdown-item w-full text-left rounded-lg border border-border"
          >
            <Icon name="Settings" size={18} className="inline-block mr-2" />
            User Settings
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="mobile-dropdown-item w-full text-left rounded-lg border border-border text-destructive"
          >
            <Icon name="LogOut" size={18} className="inline-block mr-2" />
            Logout
          </button>
        </div>
      </div>
    </>
  );
};

export default Header;
